/**
 * LABS: Pippi — "Report to Pippi", the support agent in the top bar, in Omni (ported from the back office:
 * beatly-bo-app src/components/pippi; same behaviour, same cowork API). For Beatly staff only: cowork decides
 * (GET /v1/pippi/me), the app only asks for people who could be staff (App.tsx).
 *
 * Mounted once in the app shell (App.tsx), outside the routed pages, so the panel, the conversation, an answer
 * still streaming and a Show me recording all survive navigation. The conversation id and the unsent draft live on
 * the server (cowork /v1/pippi) and come back after a reload; the open / minimised choice is per browser.
 * Nothing renders until cowork says this person is on the pilot (GET /v1/pippi/me → available).
 *
 * One conversation, one case: once its case is filed (or its how-to logged) the conversation is closed. The panel then
 * shows the case card and a "Start a new report" state; anything typed, attached or recorded goes into a NEW
 * conversation, and reopening the panel (or a reload) starts blank. Only "Add more detail" on the case card or opening
 * the case from My cases writes into it again (`adding`, sent as continueCase). cowork refuses anything else with
 * `conversation_closed`, and the panel then re-sends the text in a new conversation.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCampaign } from '@/hooks/useCampaign';
import { parseAppPath } from '../../routes';
import { acceptedFile, errorCode as codeOf, pippiApi, type CaseCard, type ConversationView, type FileKind, type PippiCase, type PippiFile, type PippiItem, type TurnBody, type TurnEvent } from './pippiApi';
import { deviceInfo, pageInfo } from './pippiContext';
import { PippiContext, type PippiValue, type StreamState } from './pippiHooks';
import { initialUi, loadUi, safeStorage, saveUi, uiReducer } from './pippiState';
import { install, recentErrors } from './pippiWatch';
import { MAX_RECORD_BYTES } from './pippiState';
// Types only: the capture code itself (pippiMedia: recorder, timeline, ears) loads on first use.
import type { RealtimeEars } from '../proposals/realtimeCall';
import type { Recording, ScreenRecorder } from './recorder';
import type { Timeline } from './timeline';
import { boAppUrl } from './pippiContext';

const loadMedia = () => import('./pippiMedia');

const EMPTY_STREAM: StreamState = { active: false, text: '', working: null, notes: [], cards: [], drafts: [], pendingUser: null };
/** A conversation untouched this long (and with no draft) is not reopened: the panel starts fresh. */
const STALE_MS = 12 * 3600_000;
const ME_POLL_MS = 60_000;
/** `busy` holds an i18n key. */
const STILL_ANSWERING = 'pippi.busy.answering';

const stamp = () => new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
/** A filed (closed) conversation takes nothing new, unless it was reopened on purpose to add to its case. */
const writable = (c: ConversationView | null, adding: boolean): c is ConversationView => !!c && (!c.closed || adding);

export function PippiProvider({ enabled, children }: { enabled: boolean; children: ReactNode }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  // The campaign's name for the page chip: the campaign page has already loaded it (react-query cache).
  const campaignId = useMemo(() => { try { return parseAppPath(location.pathname).campaignId; } catch { return undefined; } }, [location.pathname]);
  const { data: campaignData } = useCampaign({ campaignId, enabled: enabled && !!campaignId });
  const campaign = campaignData?.campaign_id === campaignId ? campaignData?.name : undefined;
  const [available, setAvailable] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [audience, setAudience] = useState<'staff' | 'client'>('staff');
  const [ui, dispatch] = useReducer(uiReducer, undefined, () => initialUi(loadUi(safeStorage())));
  const [conversation, setConversation] = useState<ConversationView | null>(null);
  const [items, setItems] = useState<PippiItem[]>([]);
  const [stream, setStream] = useState<StreamState>(EMPTY_STREAM);
  const [draft, setDraftState] = useState('');
  const [pending, setPending] = useState<PippiFile[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Why the last turn was refused, when it was (e.g. conversation_full → offer a new conversation). */
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [unread, setUnread] = useState(0);
  const [cases, setCases] = useState<PippiCase[] | null>(null);
  const [recording, setRecording] = useState<PippiValue['recording']>({ kind: null, hasVideo: false, hasVoice: false, heard: '' });
  const [dictating, setDictating] = useState(false);
  const [partial, setPartial] = useState('');
  /** The closed conversation on screen was reopened on purpose, to add to its case. */
  const [adding, setAddingState] = useState(false);
  const addingRef = useRef(false);
  const setAdding = useCallback((v: boolean) => { addingRef.current = v; setAddingState(v); }, []);

  const convRef = useRef<ConversationView | null>(null);
  convRef.current = conversation;
  const draftTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftRef = useRef('');
  draftRef.current = draft;
  const viaVoice = useRef(false);
  const recorderRef = useRef<ScreenRecorder | null>(null);
  const timelineRef = useRef<Timeline | null>(null);
  const earsRef = useRef<RealtimeEars | null>(null);
  const transcriptRef = useRef<string[]>([]);
  const dictEarsRef = useRef<RealtimeEars | null>(null);
  const page = useMemo(() => pageInfo(location.pathname, location.search, campaign), [location.pathname, location.search, campaign]);
  const pageRef = useRef(page);
  pageRef.current = page;
  // Read fresh on every render and send: the window can be resized between messages.
  const device = deviceInfo();

  // ── Gate + restore ──────────────────────────────────────────────────────
  const loadConversation = useCallback(async (id: string) => {
    const r = await pippiApi.conversation(id);
    convRef.current = r.conversation;
    setConversation(r.conversation);
    setItems(r.messages);
    setDraftState(r.conversation.draft || '');
    // A turn still running on the server (e.g. the page was reloaded mid-answer): poll until it lands.
    setBusy((b) => (r.busy ? STILL_ANSWERING : b === STILL_ANSWERING ? null : b));
    return r;
  }, []);

  useEffect(() => {
    if (!enabled) { setAvailable(false); return; }
    let live = true;
    pippiApi.me().then(async (me) => {
      if (!live) return;
      setAvailable(!!me.available);
      if (!me.available) return;
      setFirstName(me.firstName || '');
      setAudience(me.audience === 'client' ? 'client' : 'staff');
      setUnread(me.unread || 0);
      const c = me.conversation;
      const stale = c && !c.draft && Date.now() - new Date(c.updatedAt).getTime() > STALE_MS && !c.unread;
      if (c && !stale) await loadConversation(c.id).catch(() => undefined);
    }).catch(() => { if (live) setAvailable(false); });
    return () => { live = false; };
  }, [enabled, loadConversation]);

  // Recent errors go with every message: watch this tab while Pippi is available.
  useEffect(() => (available ? install() : undefined), [available]);

  // Unread support replies: a light poll; an open conversation with news is refreshed.
  useEffect(() => {
    if (!available) return;
    const t = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      pippiApi.me().then((me) => {
        setUnread(me.unread || 0);
        const open = convRef.current;
        if (open && me.conversation?.id === open.id && (me.conversation.unread || 0) > 0 && ui.mode === 'open') void loadConversation(open.id);
      }).catch(() => undefined);
    }, ME_POLL_MS);
    return () => clearInterval(t);
  }, [available, ui.mode, loadConversation]);

  useEffect(() => {
    if (busy !== STILL_ANSWERING || !conversation || stream.active) return;
    const t = setTimeout(() => { void loadConversation(conversation.id).catch(() => undefined); }, 3000);
    return () => clearTimeout(t);
  }, [busy, conversation?.id, stream.active, loadConversation]);

  // ── Navigation: follow along; ?pippi=<id> opens that conversation ─────────
  useEffect(() => {
    dispatch({ type: 'route', page });
    if (recorderRef.current && timelineRef.current) timelineRef.current.route(`${location.pathname}${location.search}`);
  }, [page]);

  useEffect(() => {
    if (!available) return;
    const params = new URLSearchParams(location.search);
    const id = params.get('pippi');
    if (!id) return;
    params.delete('pippi');
    const rest = params.toString();
    navigate({ pathname: location.pathname, search: rest ? `?${rest}` : '', hash: location.hash }, { replace: true });
    setAdding(true);
    loadConversation(id).then(() => dispatch({ type: 'open', tab: 'chat' })).catch(() => { setAdding(false); setError(t('pippi.error.notYours')); });
  }, [available, location.search]);

  useEffect(() => { saveUi(safeStorage(), ui); }, [ui.mode, ui.tab]);

  // ── Draft: saved on the server a moment after typing stops ───────────────
  /** Back to a blank start (greeting, chips, empty input): the next message makes a new conversation. */
  const resetToBlank = useCallback((keepDraft = false) => {
    convRef.current = null;
    setConversation(null);
    setItems([]);
    if (!keepDraft) setDraftState('');
    setPending([]);
    setError(null);
    setErrorCode(null);
    setAdding(false);
  }, [setAdding]);

  const ensureConversation = useCallback(async (initialDraft = ''): Promise<ConversationView> => {
    if (writable(convRef.current, addingRef.current)) return convRef.current;
    const r = await pippiApi.create(initialDraft, pageRef.current.campaignId);
    convRef.current = r.conversation;
    setConversation(r.conversation);
    setItems([]);
    setAdding(false);
    return r.conversation;
  }, [setAdding]);

  const setDraft = useCallback((s: string) => {
    setDraftState(s);
    if (draftTimer.current) clearTimeout(draftTimer.current);
    draftTimer.current = setTimeout(() => {
      const c = convRef.current;
      if (writable(c, addingRef.current)) void pippiApi.saveDraft(c.id, s).catch(() => undefined);
      // Typing under a filed case: the text stays here until it is sent (into a new conversation); the filed case stays on screen.
      else if (!c && s.trim()) void ensureConversation(s).catch(() => undefined);
    }, 800);
  }, [ensureConversation]);

  // ── Sending a turn ──────────────────────────────────────────────────────
  const runTurn = useCallback(async (body: TurnBody, shown: PippiItem, retried = false): Promise<void> => {
    const conv = await ensureConversation();
    if (draftTimer.current) clearTimeout(draftTimer.current);
    setStream({ ...EMPTY_STREAM, active: true, pendingUser: shown });
    setError(null);
    setErrorCode(null);
    const continueCase = !!conv.closed && addingRef.current;
    try {
      await pippiApi.turn(conv.id, continueCase ? { ...body, continueCase: true } : body, (e: TurnEvent) => {
        if (e.type === 'delta') setStream((s) => ({ ...s, text: s.text + e.text, working: null }));
        else if (e.type === 'working') setStream((s) => ({ ...s, working: e.label }));
        else if (e.type === 'progress') setStream((s) => ({ ...s, text: '', notes: [...s.notes, { tool: 'progress', label: e.text }] }));
        else if (e.type === 'note') setStream((s) => ({ ...s, notes: [...s.notes, e.work] }));
        else if (e.type === 'case') setStream((s) => ({ ...s, cards: [...s.cards.filter((c: CaseCard) => c.rowId !== e.card.rowId), e.card] }));
        else if (e.type === 'draft') setStream((s) => ({ ...s, drafts: [e.draft] }));
        else if (e.type === 'error') setError(e.message);
        else if (e.type === 'done') { setConversation(e.conversation); setItems(e.messages); }
      });
    } catch (e) {
      const code = codeOf(e);
      // The conversation was closed (its case filed, e.g. in another tab): this message is a new report.
      if (code === 'conversation_closed' && !retried && !body.confirm) {
        resetToBlank(true);
        const lost = (body.files || []).length > 0;
        await runTurn({ ...body, files: [] }, { ...shown, files: [] } as PippiItem, true);
        if (lost) setError(t('pippi.error.filesAgain'));
        return;
      }
      setError((e as Error).message || t('pippi.error.noAnswer'));
      setErrorCode(typeof code === 'string' ? code : null);
      await loadConversation(conv.id).catch(() => undefined);
    } finally {
      setStream(EMPTY_STREAM);
    }
  }, [ensureConversation, loadConversation, resetToBlank]);

  const send = useCallback(async (override?: string) => {
    const text = (override ?? draft).trim();
    if ((!text && !pending.length) || stream.active) return;
    const files = pending;
    const via = viaVoice.current ? 'voice' as const : 'typed' as const;
    viaVoice.current = false;
    setDraftState('');
    setPending([]);
    if (dictEarsRef.current) { dictEarsRef.current.end(); dictEarsRef.current = null; setDictating(false); setPartial(''); }
    const shown: PippiItem = { kind: 'user', id: 'pending', at: new Date().toISOString(), text, files, page: pageRef.current, via };
    await runTurn({ text, via, files: files.map((f) => f.id), page: pageRef.current, device, errors: recentErrors().slice(-8) }, shown);
  }, [draft, pending, stream.active, runTurn, device]);

  const confirmDraft = useCallback(async (draftId: string, kind: 'case' | 'escalation') => {
    if (stream.active || convRef.current?.pending?.id !== draftId) return;
    const text = t(kind === 'escalation' ? 'pippi.draft.flagSay' : 'pippi.draft.fileSay');
    const shown: PippiItem = { kind: 'user', id: 'pending', at: new Date().toISOString(), text, files: [], page: pageRef.current, via: 'typed' };
    await runTurn({ text, via: 'typed', confirm: draftId, page: pageRef.current, device, errors: recentErrors().slice(-8) }, shown);
  }, [stream.active, runTurn, device, t]);

  // ── Files, screenshots ──────────────────────────────────────────────────
  const upload = useCallback(async (list: Array<{ blob: Blob; name: string; kind: FileKind }>, retried = false): Promise<PippiFile[]> => {
    const conv = await ensureConversation();
    try {
      const r = await pippiApi.upload(conv.id, list.map((f) => ({ file: f.blob, name: f.name, kind: f.kind })), !!conv.closed && addingRef.current);
      return r.files;
    } catch (e) {
      // Closed meanwhile: the files belong to a new report.
      if (!retried && codeOf(e) === 'conversation_closed') { resetToBlank(true); return upload(list, true); }
      throw e;
    }
  }, [ensureConversation, resetToBlank]);

  const attachFiles = useCallback(async (files: Array<File | { blob: Blob; name: string }>, kind?: FileKind) => {
    if (!files.length) return;
    const wrongType = files.find((f) => (f instanceof File ? !acceptedFile(f.type, f.name) : false));
    if (wrongType) { setError(t('pippi.error.fileType', { name: wrongType.name })); return; }
    const tooBig = files.find((f) => (f instanceof File ? f.size : f.blob.size) > MAX_RECORD_BYTES);
    if (tooBig) { setError(t('pippi.error.tooBig', { name: tooBig.name })); return; }
    setBusy('pippi.busy.uploading');
    try {
      const up = await upload(files.slice(0, 5).map((f) => (f instanceof File
        ? { blob: f, name: f.name || `pasted-${stamp()}.png`, kind: kind ?? (f.type.startsWith('image/') ? 'screenshot' : f.type.startsWith('video/') ? 'recording' : 'file') }
        : { blob: f.blob, name: f.name, kind: kind ?? 'file' })));
      setPending((p) => [...p, ...up]);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  }, [upload]);

  const takeScreenshot = useCallback(async () => {
    try {
      const { captureScreenshot } = await loadMedia();
      const blob = await captureScreenshot();
      await attachFiles([{ blob, name: `screenshot-${stamp()}.png` }], 'screenshot');
    } catch (e) {
      if ((e as Error)?.name !== 'NotAllowedError') setError(t('pippi.error.screenshot'));
    }
  }, [attachFiles]);

  // ── Recording: plain screen recording, or Show me (screen + voice + transcript + action timeline) ──
  const finishRecording = useCallback(async (rec: Recording) => {
    const kind = recording.kind ?? (timelineRef.current ? 'show-me' : 'screen');
    const timeline = timelineRef.current;
    timeline?.detachAll();
    const ears = earsRef.current;
    earsRef.current = null;
    recorderRef.current = null;
    timelineRef.current = null;
    setRecording({ kind: null, hasVideo: false, hasVoice: false, heard: '' });
    dispatch({ type: 'rec/sending' });
    // Words come only after a pause: what was said just before Stop is still on its way. Commit it and wait a moment
    // (case #193: someone who talked straight through got no words at all). cowork hears the recording if none come.
    if (ears) { await ears.flush(); ears.end(); }
    const transcript = transcriptRef.current.join('\n');
    transcriptRef.current = [];
    try {
      const ext = 'webm';
      const media = rec.blob.size && rec.blob.size <= MAX_RECORD_BYTES ? [{ blob: rec.blob, name: `${kind === 'show-me' ? 'show-me' : 'screen-recording'}-${stamp()}.${ext}`, kind: (kind === 'show-me' ? 'show-me' : rec.hasVideo ? 'recording' : 'voice') as FileKind }] : [];
      if (kind === 'screen') {
        if (media.length) { const up = await upload(media); setPending((p) => [...p, ...up]); }
        dispatch({ type: 'rec/done' });
        return;
      }
      const timelineText = timeline?.toText() ?? '';
      const calls = timeline?.failedCalls() ?? [];
      const extra = [
        { blob: new Blob([JSON.stringify(timeline?.toJSON() ?? {}, null, 2)], { type: 'application/json' }), name: `timeline-${stamp()}.json`, kind: 'timeline' as FileKind },
        { blob: new Blob([timelineText || '(no actions)'], { type: 'text/plain' }), name: `timeline-${stamp()}.txt`, kind: 'timeline' as FileKind },
        ...(transcript ? [{ blob: new Blob([transcript], { type: 'text/plain' }), name: `transcript-${stamp()}.txt`, kind: 'transcript' as FileKind }] : []),
      ];
      const up = await upload([...media, ...extra]);
      const files = [...pending, ...up];
      setPending([]);
      const text = draft.trim();
      setDraftState('');
      const summary = { seconds: rec.seconds, actions: timeline?.entries.length ?? 0, failedCalls: calls.length, hasVideo: rec.hasVideo, hasVoice: rec.hasVoice };
      const shown: PippiItem = { kind: 'user', id: 'pending', at: new Date().toISOString(), text, files, page: pageRef.current, via: 'show-me', recording: summary };
      dispatch({ type: 'rec/done' });
      await runTurn({ text, via: 'show-me', files: files.map((f) => f.id), page: pageRef.current, device, errors: recentErrors().slice(-8), recording: { ...summary, transcript, timelineText, calls } }, shown);
    } catch (e) {
      setError((e as Error).message || t('pippi.error.recordingSend'));
      dispatch({ type: 'rec/failed' });
    }
  }, [recording.kind, upload, pending, draft, runTurn, device]);

  const finishRef = useRef(finishRecording);
  finishRef.current = finishRecording;

  const startRecording = useCallback(async (kind: 'screen' | 'show-me') => {
    if (recorderRef.current || stream.active) return;
    dispatch({ type: 'rec/start' });
    setError(null);
    let media: Awaited<ReturnType<typeof loadMedia>>;
    try { [media] = await Promise.all([loadMedia(), ensureConversation()]); } catch (e) { setError((e as Error).message); dispatch({ type: 'rec/failed' }); return; }
    const { ScreenRecorder, Timeline, RealtimeEars, fmt } = media;
    const recorder = new ScreenRecorder({
      screen: true, mic: kind === 'show-me',
      onTick: (s) => dispatch({ type: 'rec/tick', seconds: s }),
      onAutoStop: (r) => { dispatch({ type: 'rec/stop' }); void finishRef.current(r); },
    });
    recorderRef.current = recorder;
    try {
      const got = await recorder.start();
      // Cancelled while the browser was still asking for the screen / mic.
      if (recorderRef.current !== recorder) { recorder.cancel(); return; }
      setRecording({ kind, hasVideo: got.hasVideo, hasVoice: got.hasVoice, heard: '' });
      if (kind === 'show-me') {
        const tl = new Timeline();
        tl.start(`${window.location.pathname}${window.location.search}`);
        tl.attach(document);
        timelineRef.current = tl;
        if (got.hasVoice && recorder.micStream) {
          const ears = new RealtimeEars({
            onUtterance: (t, final) => {
              if (final) { transcriptRef.current.push(`${fmt(tl.elapsedMs)}  ${t}`); setRecording((r) => ({ ...r, heard: '' })); }
              else setRecording((r) => ({ ...r, heard: t }));
            },
            onError: () => undefined,
          });
          earsRef.current = ears;
          pippiApi.earsSecret().then((s) => ears.connect(s.clientSecret, recorder.micStream!)).catch(() => { transcriptRef.current.push('(Live transcription was not available; the voice is in the recording.)'); });
        }
      }
      dispatch({ type: 'rec/started' });
    } catch {
      recorder.cancel();
      recorderRef.current = null;
      setError(recorder.hasVideo || recorder.hasVoice ? t('pippi.error.recordingStart') : t('pippi.error.nothingToRecord'));
      dispatch({ type: 'rec/failed' });
    }
  }, [ensureConversation, stream.active]);

  const stopRecording = useCallback(async () => {
    const r = recorderRef.current;
    if (!r) return;
    if (!r.seconds) {
      // Still starting (the browser's share prompt is open): cancel, nothing to send.
      recorderRef.current = null;
      r.cancel();
      dispatch({ type: 'rec/failed' });
      return;
    }
    dispatch({ type: 'rec/stop' });
    await finishRecording(await r.stop());
  }, [finishRecording]);

  // ── Dictation into the input (the mic buttons) ─────────────────────────
  const toggleDictation = useCallback(async () => {
    if (dictEarsRef.current) { dictEarsRef.current.end(); dictEarsRef.current = null; setDictating(false); setPartial(''); return; }
    setDictating(true);
    let RealtimeEars: Awaited<ReturnType<typeof loadMedia>>['RealtimeEars'];
    try { ({ RealtimeEars } = await loadMedia()); } catch { setDictating(false); setError(t('pippi.error.mic')); return; }
    const ears = new RealtimeEars({
      onUtterance: (t, final) => {
        if (!final) { setPartial(t); return; }
        setPartial('');
        viaVoice.current = true;
        const d = draftRef.current;
        setDraft(d ? `${d.replace(/\s+$/, '')} ${t}` : t);
      },
      onError: (m) => setError(m),
      onEnded: () => { setDictating(false); setPartial(''); dictEarsRef.current = null; },
    });
    dictEarsRef.current = ears;
    pippiApi.earsSecret().then((s) => ears.connect(s.clientSecret)).catch(() => { ears.end(); setError(t('pippi.error.mic')); });
  }, [setDraft]);

  // ── Cases, conversations ──────────────────────────────────────────────
  const loadCases = useCallback(async () => {
    try { const r = await pippiApi.cases(); setCases(r.cases); setUnread(r.unread); } catch (e) { setError((e as Error).message); }
  }, []);

  /** My cases (or a ?pippi= link): that case's conversation, reopened on purpose — what is written goes to that case. */
  const openConversation = useCallback(async (id: string) => {
    try { setAdding(true); await loadConversation(id); dispatch({ type: 'open', tab: 'chat' }); } catch (e) { setAdding(false); setError((e as Error).message); }
  }, [loadConversation, setAdding]);

  /** "New report": a blank start. */
  const newConversation = useCallback(() => {
    resetToBlank();
    dispatch({ type: 'open', tab: 'chat' });
  }, [resetToBlank]);

  /** "Add more detail" on the case card: write into the filed conversation, on purpose. */
  const addDetail = useCallback(() => { setAdding(true); dispatch({ type: 'open', tab: 'chat' }); }, [setAdding]);

  // Reopening the panel after a case was filed (or a reload, where cowork hands back nothing closed) starts blank.
  const prevMode = useRef(ui.mode);
  useEffect(() => {
    const was = prevMode.current;
    prevMode.current = ui.mode;
    if (ui.mode === 'open' && was !== 'open' && convRef.current?.closed && !addingRef.current && !stream.active) resetToBlank();
  }, [ui.mode]);

  // The support book lives in the back office: open the case there, in a new tab (staff only see this button).
  const openCase = useCallback((seq: number) => { window.open(`${boAppUrl()}/product-features?case=${seq}`, '_blank', 'noopener'); }, []);

  const value: PippiValue = {
    available, firstName, audience, ui, dispatch, conversation, items, stream, draft, setDraft, pending,
    removePending: (id) => setPending((p) => p.filter((f) => f.id !== id)),
    busy, error, errorCode, clearError: () => { setError(null); setErrorCode(null); }, unread, device, cases, loadCases, send, confirmDraft, attachFiles, takeScreenshot,
    startRecording, stopRecording, recording, dictation: { active: dictating, partial, toggle: toggleDictation },
    newConversation, openConversation, openCase, addDetail,
    adding: adding && !!conversation?.closed,
    filed: !!conversation?.closed && !adding,
  };
  return <PippiContext.Provider value={value}>{children}</PippiContext.Provider>;
}
